import React from 'react';
import { Box, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Animated Background Component
 * 
 * Animated background component with dynamic gradients creating smooth visual effects:
 * - Multiple theme options (default, cyberpunk, neon, aurora, etc.)
 * - Intensity controls
 * - Gradient animations
 * - Wave animations
 * - Pulse animations
 * - Float animations
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.theme='default'] - Background theme
 * @param {string} [props.intensity='medium'] - Animation intensity
 * @returns {JSX.Element} The animated background component
 */
const AnimatedBackground = ({ theme = 'default', intensity = 'medium' }) => {
  const { _t } = useTranslation();

  
  // Animations keyframes
  const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;

  const waveAnimation = keyframes`
    0% { transform: translateX(-100%) translateY(0px); }
    50% { transform: translateX(0%) translateY(-10px); }
    100% { transform: translateX(100%) translateY(0px); }
  `;

  const pulseAnimation = keyframes`
    0% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
    100% { opacity: 0.3; transform: scale(1); }
  `;

  const floatAnimation = keyframes`
    0% { transform: translateY(0px) rotate(0deg); }
    33% { transform: translateY(-20px) rotate(120deg); }
    66% { transform: translateY(10px) rotate(240deg); }
    100% { transform: translateY(0px) rotate(360deg); }
  `;

  // Theme configuration
  const themeConfigs = {
    default: {
      gradient: 'linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #f5576c)',
      duration: '15s',
      opacity: 0.1
    },
    cyberpunk: {
      gradient: 'linear-gradient(-45deg, #00ffff, #ff00ff, #ffff00, #00bfff)',
      duration: '8s',
      opacity: 0.15,
      glow: true
    },
    neon: {
      gradient: 'linear-gradient(-45deg, #ff1493, #00bfff, #8a2be2, #ff4500)',
      duration: '10s',
      opacity: 0.2,
      pulse: true
    },
    aurora: {
      gradient: 'linear-gradient(-45deg, #00ff7f, #ff69b4, #87ceeb, #98fb98)',
      duration: '20s',
      opacity: 0.12,
      wave: true
    },
    holographic: {
      gradient: 'linear-gradient(-45deg, #9400d3, #00bfff, #ffa500, #ff1493)',
      duration: '12s',
      opacity: 0.18,
      hologram: true
    }
  };

  const config = themeConfigs[theme] || themeConfigs.default;

  // Adjust the intensity
  const intensityMultiplier = {
    low: 0.5,
    medium: 1,
    high: 1.5
  };

  const adjustedOpacity = config.opacity * (intensityMultiplier[intensity] || 1);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -2,
        overflow: 'hidden'
      }}
    >
      {/* Gradient main animated */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: config.gradient,
          backgroundSize: '400% 400%',
          opacity: adjustedOpacity,
          animation: `${gradientShift} ${config.duration} ease infinite`,
          filter: config.glow ? 'blur(1px)' : 'none'
        }}
      />

      {/* Wave effect for Aurora theme */}
      {config.wave && (
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: 0,
            width: '120%',
            height: '60%',
            background: 'linear-gradient(90deg, transparent, rgba(0,255,127,0.1), transparent)',
            animation: `${waveAnimation} 25s ease-in-out infinite`,
            transform: 'skewY(-2deg)'
          }}
        />
      )}

      {/* Pulse effect for Neon theme */}
      {config.pulse && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              right: '10%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,20,147,0.3), transparent)',
              animation: `${pulseAnimation} 4s ease-in-out infinite`
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              left: '15%',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0,255,0,0.2), transparent)',
              animation: `${pulseAnimation} 6s ease-in-out infinite reverse`
            }}
          />
        </>
      )}

      {/* Effet holographique */}
      {config.hologram && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: '30%',
              left: '20%',
              width: '100px',
              height: '100px',
              background: 'linear-gradient(45deg, rgba(148,0,211,0.4), rgba(0,191,255,0.4))',
              borderRadius: '20%',
              animation: `${floatAnimation} 15s ease-in-out infinite`,
              filter: 'blur(2px)'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              right: '25%',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(-45deg, rgba(255,165,0,0.3), rgba(255,20,147,0.3))',
              borderRadius: '30%',
              animation: `${floatAnimation} 20s ease-in-out infinite reverse`,
              filter: 'blur(3px)'
            }}
          />
        </>
      )}

      {/* Subtle overlay to improve readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02))',
          backdropFilter: 'blur(0.5px)'
        }}
      />
    </Box>
  );
};

export default React.memo(AnimatedBackground);

